import {
	createRootRoute,
	createRoute,
	redirect,
	Navigate,
	Outlet,
} from "@tanstack/react-router"

import { ProductlaneWidgetController } from "@/components/productlane-widget-controller"
import { validateAppSearch } from "@/lib/search-params"
import { AgentScreen } from "@/screens/app/agent/screen"
import { ChangelogScreen } from "@/screens/app/changelog/screen"
import { ContactsDetailScreen } from "@/screens/app/contacts/detail-screen"
import { ContactsScreen } from "@/screens/app/contacts/screen"
import { CustomersDetailScreen } from "@/screens/app/customers/detail-screen"
import { CustomersScreen } from "@/screens/app/customers/screen"
import { ProposeEditScreen } from "@/screens/app/helpCenter/propose-edit-screen"
import { HelpCenterScreen } from "@/screens/app/helpCenter/screen"
import { InboxScreen } from "@/screens/app/inbox/screen"
import { IssuesDetailScreen } from "@/screens/app/issues/detail-screen"
import { AppLayout } from "@/screens/app/layout"
import { MembersDetailScreen } from "@/screens/app/members/detail-screen"
import { NotificationsScreen } from "@/screens/app/notifications/notifications-screen"
import { ProjectDetailScreen } from "@/screens/app/projects/detail-screen"
import { ReportingScreen } from "@/screens/app/reporting/screen"
import { RoadmapScreen } from "@/screens/app/roadmap/screen"
import { SearchScreen } from "@/screens/app/search/screen"
import { SupportPortalScreen } from "@/screens/app/support-portal/screen"
import { TagsDetailScreen } from "@/screens/app/tags/detail-screen"
import { TagsScreen } from "@/screens/app/tags/screen"
import { AgentShadowModePage } from "@/screens/dev/agent-shadow-mode/agent-shadow-mode-page"
import { AuthErrorScreen } from "@/screens/misc/auth_error/page"
import { AgentKnowledgeGatePage } from "@/screens/misc/design/agent-knowledge-gate-page"
import { AgentLinearIssuePage } from "@/screens/misc/design/agent-linear-issue-page"
import { ChangelogSlackBroadcastPage } from "@/screens/misc/design/changelog-slack-broadcast-page"
import { CloseLoopByStatusPage } from "@/screens/misc/design/close-loop-by-status-page"
import { CombinedNavPage } from "@/screens/misc/design/combined-nav-page"
import { ContactFormFieldsPage } from "@/screens/misc/design/contact-form-fields-page"
import { EmptyStatePage } from "@/screens/misc/design/empty-state-page"
import { FeatureRequestLinkCardPage } from "@/screens/misc/design/feature-request-link-card-page"
import { LiveChatFirstImpressionPage } from "@/screens/misc/design/live-chat-first-impression-page"
import { NotificationSoundsPage } from "@/screens/misc/design/notification-sounds-page"
import { NotificationsEmptyPage } from "@/screens/misc/design/notifications-empty-page"
import { NotificationsIconPage } from "@/screens/misc/design/notifications-icon-page"
import { OnboardingGithubPage } from "@/screens/misc/design/onboarding-github-page"
import { OnboardingHomeDesignPage } from "@/screens/misc/design/onboarding-home-page"
import { OnboardingKnowledgePage } from "@/screens/misc/design/onboarding-knowledge-page"
import { OooReturnPreviewPage } from "@/screens/misc/design/ooo-return-preview-page" // TMP-OOO
import { PortalDigestEmailPage } from "@/screens/misc/design/portal-digest-email-page"
import { PortalSidebarFooterPage } from "@/screens/misc/design/portal-sidebar-footer-page"
import { PortalVisibilityControlsPage } from "@/screens/misc/design/portal-visibility-controls-page"
import { ReplyEmailTranscriptPage } from "@/screens/misc/design/reply-email-transcript-page"
import { RepoMapCalloutPage } from "@/screens/misc/design/repo-map-callout-page"
import { RoadmapLabelViewsPage } from "@/screens/misc/design/roadmap-label-views-page"
import { SettingsSearchCardPage } from "@/screens/misc/design/settings-search-card-page"
import { SettingsStandardPage } from "@/screens/misc/design/settings-standard-page"
import { ShadowActivateButtonPage } from "@/screens/misc/design/shadow-activate-button-page"
import { SiteImportBannerDesignPage } from "@/screens/misc/design/site-import-banner-page"
import { SlackAlertChannelsPage } from "@/screens/misc/design/slack-alert-channels-page"
import { SlackWelcomeMessagesPage } from "@/screens/misc/design/slack-welcome-messages-page"
import { SlackWorkspacesPage } from "@/screens/misc/design/slack-workspaces-page"
import { ThemeOrbPage } from "@/screens/misc/design/theme-orb-page"
import { VoiceAgentPage } from "@/screens/misc/design/voice-agent-page"
import { WidgetChatTypographyPage } from "@/screens/misc/design/widget-chat-typography-page"
import { HubspotFeedbackScreen } from "@/screens/misc/hubspot-feedback/screen"
import { IluvatarScreen } from "@/screens/misc/iluvatar/iluvatar-screen"
import { WorkspaceDetailScreen } from "@/screens/misc/iluvatar/workspace-detail-screen"
import { JoinProfileScreen } from "@/screens/misc/join-profile-screen"
import { LoginScreen } from "@/screens/misc/login/screen"
import { LogoutScreen } from "@/screens/misc/logout/screen"
import { SetupLinearScreen } from "@/screens/misc/setup-linear/setup-linear-screen"
import { SetupWorkspaceScreen } from "@/screens/misc/setup-workspace/setup-workspace-screen"
import { ToastTestPage } from "@/screens/misc/toast-test/toast-test-page"
import { UnsubscribeScreen } from "@/screens/misc/unsubscribe/page"
import { NotFound } from "@/screens/not-found"
import { McpSettingsPage } from "@/screens/settings/account/mcp-settings-page"
import { ProfileSettingsPage } from "@/screens/settings/account/profile-settings-page"
import { EmailsInboxSettingsPage } from "@/screens/settings/emails-inbox-settings"
import { EmailAddressSettingsPage } from "@/screens/settings/emails-inbox-settings/address-detail"
import { BlocklistSettingsPage } from "@/screens/settings/emails-inbox-settings/blocked-emails"
import { ForwardedEmailHandlingSettingsPage } from "@/screens/settings/emails-inbox-settings/forwarded-handling"
import { EmailSetupSettingsPage } from "@/screens/settings/emails-inbox-settings/setup"
import { AttachmentImportPage } from "@/screens/settings/import-export/attachment-import/attachment-import-page"
import { ChangelogCsvImportPage } from "@/screens/settings/import-export/changelog-csv-import/changelog-csv-import-page"
import { ContactsCsvImportPage } from "@/screens/settings/import-export/contacts-csv-import/contacts-csv-import-page"
import { CsvImportPage } from "@/screens/settings/import-export/csv-import/csv-import-page"
import { DocsCsvImportPage } from "@/screens/settings/import-export/docs-csv-import/docs-csv-import-page"
import { ImportExportSettingsPage } from "@/screens/settings/import-export/import-export-settings-page"
import { IntercomImportPage } from "@/screens/settings/import-export/intercom-import/intercom-import-page"
import { DocsMarkdownImportPage } from "@/screens/settings/import-export/markdown-import/docs-markdown-import-page"
import { ProductboardImportPage } from "@/screens/settings/import-export/productboard-import/productboard-import-page"
import { ZendeskImportPage } from "@/screens/settings/import-export/zendesk-import/zendesk-import-page"
import { ApiRequestsPage } from "@/screens/settings/integrations/api/api-activity/api-requests-page"
import { ApiIntegrationsPage } from "@/screens/settings/integrations/api/api-integration-page"
import { WebhookEditPage } from "@/screens/settings/integrations/api/webhooks/webhook-edit-page"
import { WebhookViewPage } from "@/screens/settings/integrations/api/webhooks/webhook-view-page"
import { AttioIntegrationPage } from "@/screens/settings/integrations/attio-integration-page"
import { CursorIntegrationPage } from "@/screens/settings/integrations/cursor-integration-page"
import { GithubIntegrationPage } from "@/screens/settings/integrations/github-integration-page"
import { HubspotIntegrationPage } from "@/screens/settings/integrations/hubspot-integration-page"
import { IntegrationsListPage } from "@/screens/settings/integrations/integrations-list-page"
import { IntercomIntegrationsPage } from "@/screens/settings/integrations/intercom-integration-page"
import { LinearIntegrationPage } from "@/screens/settings/integrations/linear-integration-page"
import { PlainIntegrationsPage } from "@/screens/settings/integrations/plain-integration-page"
import { PostgresIntegrationPage } from "@/screens/settings/integrations/postgres-integration-page"
import { SentryIntegrationPage } from "@/screens/settings/integrations/sentry-integration-page"
import { SlackChannelsPage } from "@/screens/settings/integrations/slack-channels-page"
import { SlackIntegrationPage } from "@/screens/settings/integrations/slack-integration-page"
import { TeamsIntegrationPage } from "@/screens/settings/integrations/teams-integration-page"
import { ZapierIntegration } from "@/screens/settings/integrations/zapier-integration-page"
import { SettingsLayout } from "@/screens/settings/layout"
import { NotificationEmailSettingsPage } from "@/screens/settings/personal-notification-settings/notification-email-page"
import { NotificationInboxSettingsPage } from "@/screens/settings/personal-notification-settings/notification-inbox-page"
import { NotificationSettingsPage } from "@/screens/settings/personal-notification-settings/notification-settings-page"
import { NotificationSlackSettingsPage } from "@/screens/settings/personal-notification-settings/notification-slack-page"
import { SsoDetailPage } from "@/screens/settings/portal-sso/sso-detail-page"
import { SettingsIndex } from "@/screens/settings/settings-index"
import { AiAssistantPage } from "@/screens/settings/settings-pages/ai-assistant-page"
import { AiChatSettingsPage } from "@/screens/settings/settings-pages/ai-chat-settings-page"
import { AiDraftsPage } from "@/screens/settings/settings-pages/ai-drafts-page"
import { AiOverviewPage } from "@/screens/settings/settings-pages/ai-overview-page"
import { AiUsagePage } from "@/screens/settings/settings-pages/ai-usage-page"
import { AssignmentsSettingsPage } from "@/screens/settings/settings-pages/assignments-settings-page"
import { BugInspectPage } from "@/screens/settings/settings-pages/bug-inspect-page"
import { ChangelogSettingsPage } from "@/screens/settings/settings-pages/changelog-settings-page"
import { CloseLoopPage } from "@/screens/settings/settings-pages/close-loop-page"
import { ConnectorPage } from "@/screens/settings/settings-pages/connectors/connector-page"
import { ConnectorSetupPage } from "@/screens/settings/settings-pages/connectors/connector-setup-page"
import { DomainSettingsPage } from "@/screens/settings/settings-pages/domain-settings-page"
import { GeneralSettingsPage } from "@/screens/settings/settings-pages/general-settings-page"
import { HelpCenterSettingsPage } from "@/screens/settings/settings-pages/help-center-settings-page"
import { LanguagesSettingsPage } from "@/screens/settings/settings-pages/languages-settings-page"
import { McpActivityPage } from "@/screens/settings/settings-pages/mcp-activity-page"
import { MemberSettingsPage } from "@/screens/settings/settings-pages/member-settings-page"
import { OfficeHoursSlaSettingsPage } from "@/screens/settings/settings-pages/office-hours-sla-settings-page"
import { PlansInvoicesPage } from "@/screens/settings/settings-pages/plans-invoices-page"
import { PlansPricingPage } from "@/screens/settings/settings-pages/plans-pricing-page"
import { PlansSettingsPage } from "@/screens/settings/settings-pages/plans-settings-page"
import { PortalInstanceSettingsPage } from "@/screens/settings/settings-pages/portal-instance-settings-page"
import { PortalInstancesSettingsPage } from "@/screens/settings/settings-pages/portal-instances-settings-page"
import { PortalSettingsPage } from "@/screens/settings/settings-pages/portal-settings-page"
import { RoadmapSettingsPage } from "@/screens/settings/settings-pages/roadmap-settings-page"
import { RoutinesPage } from "@/screens/settings/settings-pages/routines-page"
import { SidebarSectionSettingsPage } from "@/screens/settings/settings-pages/sidebar-section-settings-page"
import { SnippetFolderPage } from "@/screens/settings/settings-pages/snippet-folder-page"
import { SnippetsPage } from "@/screens/settings/settings-pages/snippets-settings-page"
import { SsoSettingsPage } from "@/screens/settings/settings-pages/sso-settings-page"
import { SupportPortalSettingsPage } from "@/screens/settings/settings-pages/support-portal-page"
import { ThemeSettingsPage } from "@/screens/settings/settings-pages/theme-settings-page"
import { WidgetSettingsPage } from "@/screens/settings/widget-settings/"
import { ZeroLoader } from "@/screens/zero-loader"

function ProductlaneWrapper() {
	return (
		<>
			<ProductlaneWidgetController />
			<Outlet />
		</>
	)
}

export const rootRoute = createRootRoute({
	validateSearch: validateAppSearch,
	component: ProductlaneWrapper,
})

const loginRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/login",
	component: LoginScreen,
})

const logoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/logout",
	component: LogoutScreen,
})

const setupLinearRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/setup-linear",
	component: SetupLinearScreen,
})

const setupWorkspaceRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/setup-workspace",
	component: SetupWorkspaceScreen,
})

const authErrorRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/auth-error",
	component: AuthErrorScreen,
})

const unsubscribeRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/unsubscribe",
	component: UnsubscribeScreen,
})

const hubspotFeedbackRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/hubspot-feedback",
	component: HubspotFeedbackScreen,
})

const toastTestRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/toast-test",
	component: ToastTestPage,
})

const designAgentKnowledgeGateRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/agent-knowledge-gate",
	component: AgentKnowledgeGatePage,
})

const designShadowActivateButtonRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/shadow-activate-button",
	component: ShadowActivateButtonPage,
})

const designChangelogSlackBroadcastRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/changelog-slack-broadcast",
	component: ChangelogSlackBroadcastPage,
})

const designRoadmapLabelViewsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/roadmap-label-views",
	component: RoadmapLabelViewsPage,
})

const designAgentLinearIssueRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/agent-linear-issue",
	component: AgentLinearIssuePage,
})

const designCloseLoopByStatusRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/close-loop-by-status",
	component: CloseLoopByStatusPage,
})

const designSettingsSearchRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/settings-search",
	component: SettingsSearchCardPage,
})

const designCombinedNavRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/combined-nav",
	component: CombinedNavPage,
})

const designContactFormFieldsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/contact-form-fields",
	component: ContactFormFieldsPage,
})

const designEmptyStateRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/empty-state",
	component: EmptyStatePage,
})

const designNotificationSoundsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/notification-sounds",
	component: NotificationSoundsPage,
})

const designNotificationsEmptyRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/notifications-empty",
	component: NotificationsEmptyPage,
})

const designNotificationsIconRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/notifications-icon",
	component: NotificationsIconPage,
})

const designRepoMapCalloutRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/repo-map-callout",
	component: RepoMapCalloutPage,
})

const designOnboardingHomeRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/onboarding-home",
	component: OnboardingHomeDesignPage,
})

const designOnboardingKnowledgeRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/onboarding-knowledge",
	component: OnboardingKnowledgePage,
})

const designOnboardingGithubRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/onboarding-github",
	component: OnboardingGithubPage,
})

const designPortalSidebarFooterRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/portal-sidebar-footer",
	component: PortalSidebarFooterPage,
})

const designPortalDigestEmailRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/portal-digest-email",
	component: PortalDigestEmailPage,
})

const designOooReturnPreviewRoute = createRoute({
	// TMP-OOO
	getParentRoute: () => rootRoute,
	path: "/design/ooo-return-preview",
	component: OooReturnPreviewPage,
})

const designSiteImportBannerRoute = createRoute({
	// TMP-OOO
	getParentRoute: () => rootRoute,
	path: "/design/site-import-banner",
	component: SiteImportBannerDesignPage,
})

const designFeatureRequestLinkCardRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/feature-request-link-card",
	component: FeatureRequestLinkCardPage,
})

const designPortalVisibilityControlsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/portal-visibility-controls",
	component: PortalVisibilityControlsPage,
})

const designReplyEmailTranscriptRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/reply-email-transcript",
	component: ReplyEmailTranscriptPage,
})

const designSlackAlertChannelsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/slack-alert-channels",
	component: SlackAlertChannelsPage,
})

const designSlackWelcomeMessagesRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/slack-welcome-messages",
	component: SlackWelcomeMessagesPage,
})

const designSettingsStandardRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/settings-standard",
	component: SettingsStandardPage,
})

const designSlackWorkspacesRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/slack-workspaces",
	component: SlackWorkspacesPage,
})

const designVoiceAgentRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/voice-agent",
	component: VoiceAgentPage,
})

const designWidgetChatTypographyRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/widget-chat-typography",
	component: WidgetChatTypographyPage,
})

const designLiveChatFirstImpressionRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/design/live-chat-first-impression",
	component: LiveChatFirstImpressionPage,
})

const devAgentShadowModeRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/dev/agent-shadow-mode",
	component: AgentShadowModePage,
})

const indexRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/",
	beforeLoad: () => {
		throw redirect({ to: "/threads", replace: true })
	},
})

const insightsRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/insights",
	beforeLoad: () => {
		throw redirect({ to: "/threads", replace: true })
	},
})

const insightsIdRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/insights/$id",
	beforeLoad: () => {
		throw redirect({ to: "/threads", replace: true })
	},
})

const projectIdRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/project/$id",
	beforeLoad: ({ params }) => {
		throw redirect({
			to: "/projects/$id",
			params: { id: params.id },
			replace: true,
		})
	},
})

const zeroLayoutRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: "zero",
	component: ZeroLoader,
})

// Inside ZeroLoader so the design shows the workspace's real colors and portal.
const designThemeOrbRoute = createRoute({
	getParentRoute: () => zeroLayoutRoute,
	path: "/design/theme-orb",
	component: ThemeOrbPage,
})

const welcomeProfileRoute = createRoute({
	getParentRoute: () => zeroLayoutRoute,
	path: "/welcome/profile",
	component: JoinProfileScreen,
})

const appLayoutRoute = createRoute({
	getParentRoute: () => zeroLayoutRoute,
	id: "app",
	component: AppLayout,
})

const threadsRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/threads",
	component: InboxScreen,
})

const threadsIdRoute = createRoute({
	getParentRoute: () => threadsRoute,
	path: "$id",
})

const notificationsRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/notifications",
	component: NotificationsScreen,
})

const notificationsKeyRoute = createRoute({
	getParentRoute: () => notificationsRoute,
	path: "$key",
})

const searchRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/search",
	component: SearchScreen,
})

const inboxTabIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/inbox/$tab/$id",
	beforeLoad: ({ params }) => {
		throw redirect({
			to: "/threads/$id",
			params: { id: params.id },
			replace: true,
		})
	},
})

const inboxSplatRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/inbox/$",
	beforeLoad: () => {
		throw redirect({ to: "/threads", replace: true })
	},
})

const feedbackIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/feedback/$id",
	beforeLoad: () => {
		throw redirect({ to: "/threads", replace: true })
	},
})

const notesRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/notes",
	beforeLoad: () => {
		throw redirect({ to: "/threads", replace: true })
	},
})

const notesIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/notes/$id",
	beforeLoad: () => {
		throw redirect({ to: "/threads", replace: true })
	},
})

const projectsRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/projects",
	beforeLoad: () => {
		throw redirect({ to: "/edit-requests", replace: true })
	},
})

const projectsIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/projects/$id",
	component: ProjectDetailScreen,
})

const issuesRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/issues",
	beforeLoad: () => {
		throw redirect({ to: "/edit-requests", replace: true })
	},
})

const issuesIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/issues/$id",
	component: IssuesDetailScreen,
})

const opportunitiesIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/opportunities/$id",
	beforeLoad: ({ params }) => {
		throw redirect({
			to: "/tags/$id",
			params: { id: params.id },
			replace: true,
		})
	},
})

const tagsRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/tags",
	beforeLoad: () => {
		throw redirect({ to: "/settings/tags", replace: true })
	},
})

const tagsIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/tags/$id",
	component: TagsDetailScreen,
})

const contactsRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/contacts",
	beforeLoad: () => {
		throw redirect({ to: "/people", replace: true })
	},
})

const contactsIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/contacts/$id",
	beforeLoad: ({ params }) => {
		throw redirect({
			to: "/people/$id",
			params: { id: params.id },
			replace: true,
		})
	},
})

const peopleRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/people",
	component: ContactsScreen,
})

const peopleIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/people/$id",
	component: ContactsDetailScreen,
})

const companiesRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/companies",
	beforeLoad: () => {
		throw redirect({ to: "/customers", replace: true })
	},
})

const companiesIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/companies/$id",
	beforeLoad: ({ params }) => {
		throw redirect({
			to: "/customers/$id",
			params: { id: params.id },
			replace: true,
		})
	},
})

const membersIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/members/$id",
	component: MembersDetailScreen,
})

const customersRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/customers",
	component: CustomersScreen,
})

const iluvatarRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/iluvatar",
	component: IluvatarScreen,
})

const iluvatarWorkspacesIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/iluvatar/workspaces/$id",
	component: WorkspaceDetailScreen,
})

const customersIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/customers/$id",
	component: CustomersDetailScreen,
})

const agentRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/agent",
	component: AgentScreen,
})

const agentTabRoute = createRoute({
	getParentRoute: () => agentRoute,
	path: "$tab",
})

const agentTabItemIdRoute = createRoute({
	getParentRoute: () => agentTabRoute,
	path: "$itemId",
})

const reportingRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/reporting",
	component: ReportingScreen,
})

const editRoadmapRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/edit-roadmap",
	beforeLoad: () => {
		throw redirect({ to: "/edit-requests", replace: true })
	},
})

const editRequestsRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/edit-requests",
	component: RoadmapScreen,
})

const supportPortalRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/support-portal",
	component: SupportPortalScreen,
})

const supportPortalContactContactIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/support-portal/contact/$contactId",
	component: SupportPortalScreen,
})

const supportPortalCompanyIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/support-portal/$companyId",
	component: SupportPortalScreen,
})

const editHelpCenterRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/edit-help-center",
	beforeLoad: () => {
		throw redirect({ to: "/edit-docs", replace: true })
	},
})

const editHelpCenterIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/edit-help-center/$id",
	beforeLoad: ({ params }) => {
		throw redirect({
			to: "/edit-docs/$id",
			params: { id: params.id },
			replace: true,
		})
	},
})

const editDocsRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/edit-docs",
	component: HelpCenterScreen,
})

const editDocsReviewArticleIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/edit-docs/review/$articleId",
	component: HelpCenterScreen,
})

const editDocsReviewNewProposalIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/edit-docs/review-new/$proposalId",
	component: HelpCenterScreen,
})

const editDocsApiTabIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/edit-docs/api/$tabId",
	component: HelpCenterScreen,
})

const editDocsApiTabIdGroupSlugSlugRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/edit-docs/api/$tabId/$groupSlug/$slug",
	component: HelpCenterScreen,
})

const editDocsIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/edit-docs/$id",
	component: HelpCenterScreen,
})

const editDocsIdProposeRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/edit-docs/$id/propose",
	component: ProposeEditScreen,
})

const editChangelogRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/edit-changelog",
	component: ChangelogScreen,
})

const editChangelogIdRoute = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "/edit-changelog/$id",
	component: ChangelogScreen,
})

const index2Route = createRoute({
	getParentRoute: () => appLayoutRoute,
	path: "$",
	component: NotFound,
})

const appLayoutRouteTree = appLayoutRoute.addChildren([
	threadsRoute.addChildren([threadsIdRoute]),
	notificationsRoute.addChildren([notificationsKeyRoute]),
	searchRoute,
	inboxTabIdRoute,
	inboxSplatRoute,
	feedbackIdRoute,
	notesRoute,
	notesIdRoute,
	projectsRoute,
	projectsIdRoute,
	issuesRoute,
	issuesIdRoute,
	opportunitiesIdRoute,
	tagsRoute,
	tagsIdRoute,
	contactsRoute,
	contactsIdRoute,
	peopleRoute,
	peopleIdRoute,
	companiesRoute,
	companiesIdRoute,
	membersIdRoute,
	customersRoute,
	iluvatarRoute,
	iluvatarWorkspacesIdRoute,
	customersIdRoute,
	agentRoute.addChildren([agentTabRoute.addChildren([agentTabItemIdRoute])]),
	reportingRoute,
	editRoadmapRoute,
	editRequestsRoute,
	supportPortalRoute,
	supportPortalContactContactIdRoute,
	supportPortalCompanyIdRoute,
	editHelpCenterRoute,
	editHelpCenterIdRoute,
	editDocsRoute,
	editDocsReviewArticleIdRoute,
	editDocsReviewNewProposalIdRoute,
	editDocsApiTabIdRoute,
	editDocsApiTabIdGroupSlugSlugRoute,
	editDocsIdRoute,
	editDocsIdProposeRoute,
	editChangelogRoute,
	editChangelogIdRoute,
	index2Route,
])

const settingsLayoutRoute = createRoute({
	getParentRoute: () => zeroLayoutRoute,
	id: "settings",
	component: SettingsLayout,
})

const settingsRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings",
	component: () => (
		<SettingsIndex desktop={<Navigate to="/settings/general" />} />
	),
})

const settingsProfileRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/profile",
	component: ProfileSettingsPage,
})

const settingsWidgetRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/widget",
	component: WidgetSettingsPage,
})

const settingsEmailsRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/emails",
	component: EmailsInboxSettingsPage,
})

const settingsEmailsAddressesAddressIdRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/emails/addresses/$addressId",
	component: EmailAddressSettingsPage,
})

const settingsEmailsSetupRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/emails/setup",
	component: EmailSetupSettingsPage,
})

const settingsEmailsBlocklistRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/emails/blocklist",
	component: BlocklistSettingsPage,
})

const settingsEmailsForwardingRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/emails/forwarding",
	component: ForwardedEmailHandlingSettingsPage,
})

const settingsEmailRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/email",
	beforeLoad: () => {
		throw redirect({ to: "/settings/emails", replace: true })
	},
})

const settingsBlocklistRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/blocklist",
	beforeLoad: () => {
		throw redirect({ to: "/settings/emails/blocklist", replace: true })
	},
})

const settingsEmailsBlockedRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/emails/blocked",
	beforeLoad: () => {
		throw redirect({ to: "/settings/emails/blocklist", replace: true })
	},
})

const settingsPortalSsoRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/portal/sso",
	component: SsoSettingsPage,
})

const settingsPortalSsoPortalIdRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/portal/sso/$portalId",
	component: SsoDetailPage,
})

const settingsSsoRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/sso",
	beforeLoad: () => {
		throw redirect({ to: "/settings/portal/sso", replace: true })
	},
})

const settingsGeneralRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/general",
	component: GeneralSettingsPage,
})

const settingsNotificationsRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/notifications",
	component: NotificationSettingsPage,
})

const settingsNotificationsInboxRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/notifications/inbox",
	component: NotificationInboxSettingsPage,
})

const settingsNotificationsEmailRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/notifications/email",
	component: NotificationEmailSettingsPage,
})

const settingsNotificationsSlackRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/notifications/slack",
	component: NotificationSlackSettingsPage,
})

const settingsMcpRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/mcp",
	component: McpSettingsPage,
})

const settingsSignatureRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/signature",
	beforeLoad: () => {
		throw redirect({ to: "/settings/profile", replace: true })
	},
})

const settingsInboxRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/inbox",
	beforeLoad: () => {
		throw redirect({ to: "/settings/profile", replace: true })
	},
})

const settingsPlansRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/plans",
	component: PlansSettingsPage,
})

const settingsPlansPricingRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/plans/pricing",
	component: PlansPricingPage,
})

const settingsPlansInvoicesRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/plans/invoices",
	component: PlansInvoicesPage,
})

const settingsMembersRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/members",
	component: MemberSettingsPage,
})

const settingsTransferRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/transfer",
	component: ImportExportSettingsPage,
})

const settingsContactsCsvImportRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/contacts-csv-import",
	component: ContactsCsvImportPage,
})

const settingsCsvImportRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/csv-import",
	component: CsvImportPage,
})

const settingsChangelogCsvImportRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/changelog-csv-import",
	component: ChangelogCsvImportPage,
})

const settingsDocsCsvImportRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/docs-csv-import",
	component: DocsCsvImportPage,
})

const settingsDocsMarkdownImportRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/docs-markdown-import",
	component: DocsMarkdownImportPage,
})

const settingsAttachmentImportRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/attachment-import",
	component: AttachmentImportPage,
})

const settingsProductboardImportRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/productboard-import",
	component: ProductboardImportPage,
})

const settingsZendeskImportRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/zendesk-import",
	component: ZendeskImportPage,
})

const settingsIntercomImportRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/intercom-import",
	component: IntercomImportPage,
})

const settingsMacrosRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/macros",
	component: SnippetsPage,
})

const settingsMacrosFolderIdRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/macros/$folderId",
	component: SnippetFolderPage,
})

const settingsSnippetsRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/snippets",
	beforeLoad: () => {
		throw redirect({ to: "/settings/macros", replace: true })
	},
})

const settingsSnippetsFolderIdRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/snippets/$folderId",
	beforeLoad: ({ params }) => {
		throw redirect({
			to: "/settings/macros/$folderId",
			params: { folderId: params.folderId },
			replace: true,
		})
	},
})

const settingsIntegrationsRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/integrations",
	component: IntegrationsListPage,
})

const settingsIntegrationsZapierRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/integrations/zapier",
	component: ZapierIntegration,
})

const settingsIntegrationsTeamsRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/integrations/teams",
	component: TeamsIntegrationPage,
})

const settingsCustomConnectorsRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/custom-connectors",
	component: SidebarSectionSettingsPage,
})

const settingsCustomConnectorsHistoryRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/custom-connectors/history",
	component: McpActivityPage,
})

const settingsCustomConnectorsNewRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/custom-connectors/new",
	component: ConnectorSetupPage,
})

const settingsCustomConnectorRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/custom-connectors/$connectorId",
	component: ConnectorPage,
})

const settingsSidebarSectionsRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/sidebar-sections",
	beforeLoad: () => {
		throw redirect({ to: "/settings/custom-connectors", replace: true })
	},
})

const settingsIntegrationsPlainRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/integrations/plain",
	component: PlainIntegrationsPage,
})

const settingsIntegrationsPostgresRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/integrations/postgres",
	component: PostgresIntegrationPage,
})

const settingsIntegrationsSlackRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/integrations/slack",
	component: SlackIntegrationPage,
})

const settingsIntegrationsSlackConnectionIdChannelsRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/integrations/slack/$connectionId/channels",
	component: SlackChannelsPage,
})

const settingsIntegrationsIntercomRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/integrations/intercom",
	component: IntercomIntegrationsPage,
})

const settingsIntegrationsCursorRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/integrations/cursor",
	component: CursorIntegrationPage,
})

const settingsIntegrationsGithubRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/integrations/github",
	component: GithubIntegrationPage,
})

const settingsIntegrationsApiRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/integrations/api",
	component: ApiIntegrationsPage,
})

const settingsIntegrationsApiRequestsRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/integrations/api/requests",
	component: ApiRequestsPage,
})

const settingsIntegrationsApiWebhooksIdRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/integrations/api/webhooks/$id",
	component: WebhookViewPage,
})

const settingsIntegrationsApiWebhooksIdEditRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/integrations/api/webhooks/$id/edit",
	component: WebhookEditPage,
})

const settingsIntegrationsLinearRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/integrations/linear",
	component: LinearIntegrationPage,
})

const settingsIntegrationsHubspotRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/integrations/hubspot",
	component: HubspotIntegrationPage,
})

const settingsIntegrationsAttioRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/integrations/attio",
	component: AttioIntegrationPage,
})

const settingsIntegrationsSentryRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/integrations/sentry",
	component: SentryIntegrationPage,
})

const settingsPortalRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/portal",
	component: PortalSettingsPage,
})

const settingsPortalThemeRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/portal/theme",
	component: ThemeSettingsPage,
})

const settingsPortalDomainRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/portal/domain",
	component: DomainSettingsPage,
})

const settingsPortalInstancesRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/portal/instances",
	component: PortalInstancesSettingsPage,
})

const settingsPortalInstancesPortalIdRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/portal/instances/$portalId",
	component: PortalInstanceSettingsPage,
})

const settingsLanguagesRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/languages",
	component: LanguagesSettingsPage,
})

const settingsThemeRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/theme",
	beforeLoad: () => {
		throw redirect({ to: "/settings/portal/theme", replace: true })
	},
})

const settingsDomainRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/domain",
	beforeLoad: () => {
		throw redirect({ to: "/settings/portal/domain", replace: true })
	},
})

const settingsMultilingualRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/multilingual",
	beforeLoad: () => {
		throw redirect({ to: "/settings/languages", replace: true })
	},
})

const settingsPortalInstances2Route = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/portal-instances",
	beforeLoad: () => {
		throw redirect({ to: "/settings/portal/instances", replace: true })
	},
})

const settingsZapierRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/zapier",
	beforeLoad: () => {
		throw redirect({ to: "/settings/integrations/zapier", replace: true })
	},
})

const settingsTeamsRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/teams",
	beforeLoad: () => {
		throw redirect({ to: "/settings/integrations/teams", replace: true })
	},
})

const settingsPlainRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/plain",
	beforeLoad: () => {
		throw redirect({ to: "/settings/integrations/plain", replace: true })
	},
})

const settingsPostgresRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/postgres",
	beforeLoad: () => {
		throw redirect({ to: "/settings/integrations/postgres", replace: true })
	},
})

const settingsSlackRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/slack",
	beforeLoad: () => {
		throw redirect({ to: "/settings/integrations/slack", replace: true })
	},
})

const settingsIntercomRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/intercom",
	beforeLoad: () => {
		throw redirect({ to: "/settings/integrations/intercom", replace: true })
	},
})

const settingsCursorRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/cursor",
	beforeLoad: () => {
		throw redirect({ to: "/settings/integrations/cursor", replace: true })
	},
})

const settingsGithubRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/github",
	beforeLoad: () => {
		throw redirect({ to: "/settings/integrations/github", replace: true })
	},
})

const settingsApiRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/api",
	beforeLoad: () => {
		throw redirect({ to: "/settings/integrations/api", replace: true })
	},
})

const settingsLinearRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/linear",
	beforeLoad: () => {
		throw redirect({ to: "/settings/integrations/linear", replace: true })
	},
})

const settingsHubspotRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/hubspot",
	beforeLoad: () => {
		throw redirect({ to: "/settings/integrations/hubspot", replace: true })
	},
})

const settingsOfficeHoursRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/office-hours",
	component: OfficeHoursSlaSettingsPage,
})

const settingsAssignmentsRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/assignments",
	component: AssignmentsSettingsPage,
})

const settingsSlaRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/sla",
	beforeLoad: () => {
		throw redirect({ to: "/settings/office-hours", replace: true })
	},
})

const settingsChangelogRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/changelog",
	component: ChangelogSettingsPage,
})

const settingsKnowledgeBaseRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/knowledge-base",
	beforeLoad: () => {
		throw redirect({ to: "/settings/docs", replace: true })
	},
})

const settingsDocsRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/docs",
	component: HelpCenterSettingsPage,
})

const settingsAiRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/ai",
	component: AiOverviewPage,
})

const settingsAiAgentRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/ai/agent",
	component: AiChatSettingsPage,
})

const settingsAiAssistantRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/ai/assistant",
	component: AiAssistantPage,
})

const settingsAiCopilotRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/ai/copilot",
	beforeLoad: () => {
		throw redirect({ to: "/settings/ai/assistant", replace: true })
	},
})

const settingsAiCloseLoopRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/ai/close-loop",
	component: CloseLoopPage,
})

const settingsAiRoutinesRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/ai/routines",
	component: RoutinesPage,
})

const settingsAiDraftsRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/ai/drafts",
	component: AiDraftsPage,
})

const settingsAiUsageRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/ai/usage",
	component: AiUsagePage,
})

const settingsAiBugInspectRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/ai/bug-inspect",
	component: BugInspectPage,
})

const settingsAiChatRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/ai-chat",
	beforeLoad: () => {
		throw redirect({ to: "/settings/ai/agent", replace: true })
	},
})

const settingsAiAgent2Route = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/ai-agent",
	beforeLoad: () => {
		throw redirect({ to: "/settings/ai/agent", replace: true })
	},
})

const settingsAiCopilot2Route = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/ai-copilot",
	beforeLoad: () => {
		throw redirect({ to: "/settings/ai/drafts", replace: true })
	},
})

const settingsLiveChatRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/live-chat",
	beforeLoad: () => {
		throw redirect({ to: "/settings/widget", replace: true })
	},
})

const settingsRoadmapRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/roadmap",
	component: RoadmapSettingsPage,
})

const settingsSupportPortalRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/support-portal",
	component: SupportPortalSettingsPage,
})

const settingsTagsRoute = createRoute({
	getParentRoute: () => settingsLayoutRoute,
	path: "/settings/tags",
	component: TagsScreen,
})

const settingsLayoutRouteTree = settingsLayoutRoute.addChildren([
	settingsRoute,
	settingsProfileRoute,
	settingsWidgetRoute,
	settingsEmailsRoute,
	settingsEmailsAddressesAddressIdRoute,
	settingsEmailsSetupRoute,
	settingsEmailsBlocklistRoute,
	settingsEmailsForwardingRoute,
	settingsEmailRoute,
	settingsBlocklistRoute,
	settingsEmailsBlockedRoute,
	settingsPortalSsoRoute,
	settingsPortalSsoPortalIdRoute,
	settingsSsoRoute,
	settingsGeneralRoute,
	settingsNotificationsRoute,
	settingsNotificationsInboxRoute,
	settingsNotificationsEmailRoute,
	settingsNotificationsSlackRoute,
	settingsMcpRoute,
	settingsSignatureRoute,
	settingsInboxRoute,
	settingsPlansRoute,
	settingsPlansPricingRoute,
	settingsPlansInvoicesRoute,
	settingsMembersRoute,
	settingsTransferRoute,
	settingsContactsCsvImportRoute,
	settingsCsvImportRoute,
	settingsChangelogCsvImportRoute,
	settingsDocsCsvImportRoute,
	settingsDocsMarkdownImportRoute,
	settingsAttachmentImportRoute,
	settingsProductboardImportRoute,
	settingsZendeskImportRoute,
	settingsIntercomImportRoute,
	settingsMacrosRoute,
	settingsMacrosFolderIdRoute,
	settingsSnippetsRoute,
	settingsSnippetsFolderIdRoute,
	settingsIntegrationsRoute,
	settingsIntegrationsZapierRoute,
	settingsIntegrationsTeamsRoute,
	settingsCustomConnectorsRoute,
	settingsCustomConnectorsHistoryRoute,
	settingsCustomConnectorsNewRoute,
	settingsCustomConnectorRoute,
	settingsSidebarSectionsRoute,
	settingsIntegrationsPlainRoute,
	settingsIntegrationsPostgresRoute,
	settingsIntegrationsSlackRoute,
	settingsIntegrationsSlackConnectionIdChannelsRoute,
	settingsIntegrationsIntercomRoute,
	settingsIntegrationsCursorRoute,
	settingsIntegrationsGithubRoute,
	settingsIntegrationsApiRoute,
	settingsIntegrationsApiRequestsRoute,
	settingsIntegrationsApiWebhooksIdRoute,
	settingsIntegrationsApiWebhooksIdEditRoute,
	settingsIntegrationsLinearRoute,
	settingsIntegrationsHubspotRoute,
	settingsIntegrationsAttioRoute,
	settingsIntegrationsSentryRoute,
	settingsPortalRoute,
	settingsPortalThemeRoute,
	settingsPortalDomainRoute,
	settingsPortalInstancesRoute,
	settingsPortalInstancesPortalIdRoute,
	settingsLanguagesRoute,
	settingsThemeRoute,
	settingsDomainRoute,
	settingsMultilingualRoute,
	settingsPortalInstances2Route,
	settingsZapierRoute,
	settingsTeamsRoute,
	settingsPlainRoute,
	settingsPostgresRoute,
	settingsSlackRoute,
	settingsIntercomRoute,
	settingsCursorRoute,
	settingsGithubRoute,
	settingsApiRoute,
	settingsLinearRoute,
	settingsHubspotRoute,
	settingsOfficeHoursRoute,
	settingsAssignmentsRoute,
	settingsSlaRoute,
	settingsChangelogRoute,
	settingsKnowledgeBaseRoute,
	settingsDocsRoute,
	settingsAiRoute,
	settingsAiAgentRoute,
	settingsAiAssistantRoute,
	settingsAiCopilotRoute,
	settingsAiCloseLoopRoute,
	settingsAiRoutinesRoute,
	settingsAiDraftsRoute,
	settingsAiUsageRoute,
	settingsAiBugInspectRoute,
	settingsAiChatRoute,
	settingsAiAgent2Route,
	settingsAiCopilot2Route,
	settingsLiveChatRoute,
	settingsRoadmapRoute,
	settingsSupportPortalRoute,
	settingsTagsRoute,
])

const zeroLayoutRouteTree = zeroLayoutRoute.addChildren([
	designThemeOrbRoute,
	welcomeProfileRoute,
	appLayoutRouteTree,
	settingsLayoutRouteTree,
])

const rootRouteTree = rootRoute.addChildren([
	loginRoute,
	logoutRoute,
	setupLinearRoute,
	setupWorkspaceRoute,
	authErrorRoute,
	unsubscribeRoute,
	hubspotFeedbackRoute,
	toastTestRoute,
	designAgentKnowledgeGateRoute,
	designShadowActivateButtonRoute,
	designChangelogSlackBroadcastRoute,
	designRoadmapLabelViewsRoute,
	designAgentLinearIssueRoute,
	designCloseLoopByStatusRoute,
	designSettingsSearchRoute,
	designCombinedNavRoute,
	designContactFormFieldsRoute,
	designEmptyStateRoute,
	designNotificationSoundsRoute,
	designNotificationsEmptyRoute,
	designNotificationsIconRoute,
	designRepoMapCalloutRoute,
	designOnboardingHomeRoute,
	designOnboardingKnowledgeRoute,
	designOnboardingGithubRoute,
	designPortalSidebarFooterRoute,
	designPortalDigestEmailRoute,
	designOooReturnPreviewRoute,
	designSiteImportBannerRoute,
	designFeatureRequestLinkCardRoute,
	designPortalVisibilityControlsRoute,
	designReplyEmailTranscriptRoute,
	designSlackAlertChannelsRoute,
	designSlackWelcomeMessagesRoute,
	designSettingsStandardRoute,
	designSlackWorkspacesRoute,
	designVoiceAgentRoute,
	designWidgetChatTypographyRoute,
	designLiveChatFirstImpressionRoute,
	devAgentShadowModeRoute,
	indexRoute,
	insightsRoute,
	insightsIdRoute,
	projectIdRoute,
	zeroLayoutRouteTree,
])

export const routeTree = rootRouteTree
